/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { IconProps } from '@coursera/cds-common';
import { Button, Dialog, TextField, Typography2 } from '@coursera/cds-core';
import { BriefcaseIcon, EyeIcon, FlagIcon, MaximizeIcon, MinimizeIcon, SmileIcon } from '@coursera/cds-icons';

import type {
  AIMessageDraft,
  MessageRewriteAction,
} from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

type Props = {
  draft: AIMessageDraft;
  updateDraft: (partial: Partial<AIMessageDraft>) => void;
  onSend: () => void;
  onBack: () => void;
  /**
   * Invoked when the admin picks one of the message-rewrite action pills
   * (Shorten / Expand / More casual / More formal / Add urgency). Stub
   * until the rewrite endpoint ships; the pills stay clickable either way.
   */
  onMessageAction?: (action: MessageRewriteAction) => void;
  /**
   * Hook for the "View learner list" link. Stays enabled per design — falls
   * back to a no-op when the screen isn't wired up yet.
   */
  onViewLearnerList?: () => void;
  /**
   * Hook for the "Preview email" link. Stays enabled per design — falls
   * back to a no-op when the screen isn't wired up yet.
   */
  onPreviewEmail?: () => void;
};

const styles = {
  body: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  field: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
  `,
  audienceRow: css`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--cds-spacing-200);
  `,
  fieldLabel: css`
    color: var(--cds-color-neutral-primary);
  `,
  actionPills: css`
    display: flex;
    flex-wrap: wrap;
    gap: var(--cds-spacing-100);
  `,
  /* Pill-shaped trigger that fires a message-rewrite action. Per Figma:
     fully-rounded outline, 1px neutral border, dark text + icon, white bg. */
  actionPill: css`
    display: inline-flex;
    align-items: center;
    gap: var(--cds-spacing-50);
    height: 36px;
    padding: 0 var(--cds-spacing-200);
    background: var(--cds-color-neutral-background-primary);
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: 9999px;
    color: var(--cds-color-neutral-primary);
    cursor: pointer;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    white-space: nowrap;

    &:hover:not(:disabled) {
      background: var(--cds-color-neutral-disabled-weak);
    }

    &:focus-visible {
      outline: 2px solid var(--cds-color-interactive-stroke-primary-focus);
      outline-offset: 2px;
    }
  `,
  ctaRow: css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--cds-spacing-200);
  `,
  actions: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-150);
    width: 100%;
  `,
  previewLink: css`
    margin-left: auto;
  `,
};

type ActionConfig = {
  action: MessageRewriteAction;
  label: string;
  Icon: React.ComponentType<IconProps>;
};

const getActions = (): ActionConfig[] => [
  { action: 'shorten', label: _t('Shorten'), Icon: MinimizeIcon },
  { action: 'expand', label: _t('Expand'), Icon: MaximizeIcon },
  { action: 'more_casual', label: _t('More casual'), Icon: SmileIcon },
  { action: 'more_formal', label: _t('More formal'), Icon: BriefcaseIcon },
  { action: 'add_urgency', label: _t('Add urgency'), Icon: FlagIcon },
];

/**
 * Step 2 of the AI Message modal: the admin reviews the auto-generated
 * audience / subject / message, can run one of five rewrite actions on the
 * message body (Shorten / Expand / More casual / More formal / Add urgency),
 * and then sends. "View learner list" and "Preview email" stay clickable
 * per design intent; their handlers are stubbed until those screens ship.
 */
const ReviewAndSendScreen = ({
  draft,
  updateDraft,
  onSend,
  onBack,
  onMessageAction,
  onViewLearnerList,
  onPreviewEmail,
}: Props) => {
  const actions = React.useMemo(() => getActions(), []);

  return (
    <React.Fragment>
      <Dialog.Content>
        <div css={styles.body}>
          <div css={styles.audienceRow}>
            <div css={styles.field}>
              <Typography2 component="span" variant="subtitleMedium" css={styles.fieldLabel}>
                {_t('Audience')}
              </Typography2>
              <Typography2 component="span" variant="bodyPrimary">
                {draft.audienceSummary}
              </Typography2>
            </div>
            <Button
              variant="ghost"
              size="small"
              onClick={onViewLearnerList}
              data-testid="ai-message-modal-view-learner-list"
            >
              {_t('View learner list')}
            </Button>
          </div>

          <div css={styles.field}>
            <Typography2 component="span" variant="subtitleMedium" css={styles.fieldLabel}>
              {_t('Subject')}
            </Typography2>
            <TextField
              aria-label={_t('Subject')}
              value={draft.subject}
              onChange={(event) => updateDraft({ subject: event.target.value })}
              data-testid="ai-message-modal-subject"
            />
          </div>

          <div css={styles.field}>
            <Typography2 component="span" variant="subtitleMedium" css={styles.fieldLabel}>
              {_t('Message')}
            </Typography2>
            <div css={styles.actionPills}>
              {actions.map(({ action, label, Icon }) => (
                <button
                  key={action}
                  type="button"
                  css={styles.actionPill}
                  onClick={() => onMessageAction?.(action)}
                  data-testid={`ai-message-modal-action-${action}`}
                >
                  {/* Icon is decorative; the visible label already names
                      the action — hide from AT to avoid double-announce. */}
                  <Icon size="small" aria-hidden="true" />
                  {label}
                </button>
              ))}
            </div>
            <TextField
              aria-label={_t('Message body')}
              multiline
              minRows={6}
              value={draft.message}
              onChange={(event) => updateDraft({ message: event.target.value })}
              data-testid="ai-message-modal-message-body"
            />
          </div>

          <div css={styles.ctaRow}>
            <TextField
              label={_t('Call to action')}
              value={draft.ctaText}
              onChange={(event) => updateDraft({ ctaText: event.target.value })}
              data-testid="ai-message-modal-cta-text"
            />
            <TextField
              label={_t('Button URL')}
              value={draft.ctaUrl}
              onChange={(event) => updateDraft({ ctaUrl: event.target.value })}
              type="url"
              autoComplete="url"
              inputProps={{ inputMode: 'url' }}
              data-testid="ai-message-modal-cta-url"
            />
          </div>
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <div css={styles.actions}>
          <Button variant="primary" onClick={onSend} data-testid="ai-message-modal-send">
            {_t('Send')}
          </Button>
          <Button variant="ghost" onClick={onBack} data-testid="ai-message-modal-back">
            {_t('Back')}
          </Button>
          <Button
            variant="ghost"
            onClick={onPreviewEmail}
            icon={<EyeIcon />}
            iconPosition="before"
            css={styles.previewLink}
            data-testid="ai-message-modal-preview-email"
          >
            {_t('Preview email')}
          </Button>
        </div>
      </Dialog.Actions>
    </React.Fragment>
  );
};

export default ReviewAndSendScreen;
